import { format, isValid, parseISO } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import StaffDetails from './individalstaff';


const caseTypes = ['HR', 'Absense', 'Maternity', 'Lateness', 'Discplinary'];

const toDate = (value) => {
  if (!value) return '';

  const raw = String(value).trim();
  const parsedIso = parseISO(raw);
  if (isValid(parsedIso)) return parsedIso;

  const parsed = new Date(raw);
  return isValid(parsed) ? parsed : null;
};

const toYmd = (value) => {
  const parsed = toDate(value);
  return parsed ? format(parsed, 'yyyy-MM-dd') : '';
};

const toDisplayDate = (value) => {
  const parsed = toDate(value);
  return parsed ? format(parsed, 'dd/MM/yyyy') : '-';
};

export default function HasHR({ onBack }) {
  const [staff, setStaff] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [disciplinaryCases, setDisciplinaryCases] = useState([]);
  const [savedCases, setSavedCases] = useState([]);
  const [meetingLoading, setMeetingLoading] = useState(false);
  const [disciplinaryLoading, setDisciplinaryLoading] = useState(false);
  const [casesLoading, setCasesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [staffProfileLoading, setStaffProfileLoading] = useState(false);
  const [selectedStaffProfile, setSelectedStaffProfile] = useState(null);
  const [message, setMessage] = useState('');
  const [expandedMeetingNotes, setExpandedMeetingNotes] = useState({});

  const [formData, setFormData] = useState({
    staffId: '',
    type: 'HR',
    comments: '',
    timeline: '',
    originalMeetingDate: '',
    meetingId: '',
    disciplinaryId: '',
  });

  const isDisciplinaryType =
    String(formData.type || '').toLowerCase() === 'discplinary' ||
    String(formData.type || '').toLowerCase() === 'disciplinary';

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const response = await fetch('https://api.insightos.co.uk/api/staff', {
          method: 'GET',
          credentials: 'include',
        });

        const data = await response.json();
        if (data.success) {
          setStaff(Array.isArray(data.data) ? data.data : []);
        }
      } catch (error) {
        console.error('Failed to fetch staff', error);
      }
    };

    fetchStaff();
  }, []);

  useEffect(() => {
    const fetchCases = async () => {
      setCasesLoading(true);
      try {
        const response = await fetch('https://api.insightos.co.uk/api/hashr', {
          method: 'GET',
          credentials: 'include',
        });

        const data = await response.json();
        if (data.success) {
          setSavedCases(Array.isArray(data.data) ? data.data : []);
        } else {
          setSavedCases([]);
        }
      } catch (error) {
        console.error('Failed to fetch HR cases', error);
        setSavedCases([]);
      } finally {
        setCasesLoading(false);
      }
    };

    fetchCases();
  }, []);

  useEffect(() => {
    const fetchMeetings = async () => {
      const normalizedMeetingDate = toYmd(formData.originalMeetingDate);
      if (!normalizedMeetingDate) {
        setMeetings([]);
        setFormData((prev) => ({ ...prev, meetingId: '' }));
        return;
      }

      setMeetingLoading(true);
      try {
        const response = await fetch(`https://api.insightos.co.uk/api/meetings/${encodeURIComponent(normalizedMeetingDate)}`, {
          method: 'GET',
          credentials: 'include',
        });

        const data = await response.json();
        if (data.success) {
          const allMeetings = Array.isArray(data.data) ? data.data : [];
          const disciplinaryMeetings = allMeetings.filter(
            (meeting) => String(meeting.Type || '').trim().toLowerCase() === 'disciplinary'
          );
          setMeetings(disciplinaryMeetings);

          setFormData((prev) => {
            const hasMatch = disciplinaryMeetings.some((meeting) => String(meeting.id) === String(prev.meetingId));
            return hasMatch ? prev : { ...prev, meetingId: '' };
          });
        } else {
          setMeetings([]);
        }
      } catch (error) {
        console.error('Failed to fetch meetings', error);
        setMeetings([]);
      } finally {
        setMeetingLoading(false);
      }
    };

    fetchMeetings();
  }, [formData.originalMeetingDate]);

  useEffect(() => {
    const fetchDisciplinaryCases = async () => {
      if (!isDisciplinaryType) {
        setDisciplinaryCases([]);
        setFormData((prev) => ({ ...prev, disciplinaryId: '' }));
        return;
      }

      setDisciplinaryLoading(true);
      try {
        const response = await fetch('https://api.insightos.co.uk/api/disciplinarymanager', {
          method: 'GET',
          credentials: 'include',
        });

        const data = await response.json();
        if (data.success) {
          const cases = Array.isArray(data.data) ? data.data : [];
          setDisciplinaryCases(cases);

          setFormData((prev) => {
            const hasMatch = cases.some((item) => String(item.id) === String(prev.disciplinaryId));
            return hasMatch ? prev : { ...prev, disciplinaryId: '' };
          });
        } else {
          setDisciplinaryCases([]);
        }
      } catch (error) {
        console.error('Failed to fetch disciplinary cases', error);
        setDisciplinaryCases([]);
      } finally {
        setDisciplinaryLoading(false);
      }
    };

    fetchDisciplinaryCases();
  }, [isDisciplinaryType]);

  const selectedMeeting = useMemo(
    () => meetings.find((meeting) => String(meeting.id) === String(formData.meetingId)),
    [meetings, formData.meetingId]
  );

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setMessage('');
  };

  const toggleMeetingNotes = (caseId) => {
    setExpandedMeetingNotes((prev) => ({
      ...prev,
      [caseId]: !prev[caseId],
    }));
  };

  const statusClassName = (status) => {
    const normalized = String(status || '').trim().toLowerCase();
    if (normalized === 'awaiting response') return 'bg-red-200 text-red-900';
    if (normalized === 'response recieved') return 'bg-emerald-200 text-emerald-900';
    return 'bg-gray-200 text-gray-900';
  };

  const handleOpenIndividualStaff = async (staffId) => {
    if (!staffId) return;

    setStaffProfileLoading(true);
    setMessage('');
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/staffprofile/${staffId}`, {
        method: 'GET',
        credentials: 'include',
      });

      const data = await response.json();
      if (!response.ok || !data.success || !Array.isArray(data.data) || data.data.length === 0) {
        throw new Error(data.message || 'Unable to load staff profile');
      }

      setSelectedStaffProfile(data.data[0]);
    } catch (error) {
      console.error('Failed to fetch staff profile', error);
      setMessage(error.message || 'Failed to open staff profile.');
    } finally {
      setStaffProfileLoading(false);
    }
  };

  const handleSaveCase = async () => {
    if (!formData.staffId || !formData.type || !formData.timeline || !formData.originalMeetingDate) {
      setMessage('Please complete Staff, Type, Timeline, and Original Meeting Date.');
      return;
    }

    setSaving(true);
    setMessage('');

    try {
      const response = await fetch('https://api.insightos.co.uk/api/hashr', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          MeetingId: formData.meetingId || null,
          DisciplinaryId: formData.disciplinaryId || null,
          StaffId: formData.staffId,
          Type: formData.type,
          Timeline: toYmd(formData.timeline),
          OriginalMeetingDate: toYmd(formData.originalMeetingDate),
          Comments: formData.comments,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to save HR case');
      }

      setMessage('Case saved successfully.');
      setFormData((prev) => ({
        ...prev,
        comments: '',
        timeline: '',
        originalMeetingDate: '',
        meetingId: '',
        disciplinaryId: '',
      }));
      setMeetings([]);

      const casesResponse = await fetch('https://api.insightos.co.uk/api/hashr', {
        method: 'GET',
        credentials: 'include',
      });
      const casesData = await casesResponse.json();
      if (casesData.success) {
        setSavedCases(Array.isArray(casesData.data) ? casesData.data : []);
      }
    } catch (error) {
      console.error('Failed to save HR case', error);
      setMessage(error.message || 'Failed to save HR case.');
    } finally {
      setSaving(false);
    }
  };

  if (selectedStaffProfile) {
    return (
      <div className="w-full">
        <div className="mb-4 flex justify-end">
          <button
            type="button"
            onClick={() => setSelectedStaffProfile(null)}
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
          >
            Back to HR Cases
          </button>
        </div>
        <StaffDetails staff={selectedStaffProfile} />
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto rounded-2xl bg-white shadow-lg border border-gray-200 p-6 sm:p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-900">Create Case</h2>
        <button
          type="button"
          onClick={onBack}
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
        >
          Back
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Staff</label>
          <select
            value={formData.staffId}
            onChange={(e) => handleChange('staffId', e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">Select staff member</option>
            {staff.map((member) => (
              <option key={member.id} value={member.id}>
                {member.Name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
          <select
            value={formData.type}
            onChange={(e) => handleChange('type', e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            {caseTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Timeline</label>
          <input
            type="date"
            value={formData.timeline}
            onChange={(e) => handleChange('timeline', e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Original Meeting Date</label>
          <input
            type="date"
            value={formData.originalMeetingDate}
            onChange={(e) => handleChange('originalMeetingDate', e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Comments</label>
        <textarea
          value={formData.comments}
          onChange={(e) => handleChange('comments', e.target.value)}
          rows={15}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          placeholder="Enter case comments"
        />
      </div>

      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Disciplinary Meeting</label>
        <select
          value={formData.meetingId}
          onChange={(e) => handleChange('meetingId', e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          disabled={!formData.originalMeetingDate || meetingLoading}
        >
          <option value="">{meetingLoading ? 'Loading meetings...' : 'Select disciplinary meeting'}</option>
          {meetings.map((meeting) => (
            <option key={meeting.id} value={meeting.id}>
              {meeting.Name} - {meeting.Attendees}
            </option>
          ))}
        </select>
      </div>

      {isDisciplinaryType && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Disciplinary Case</label>
          <select
            value={formData.disciplinaryId}
            onChange={(e) => handleChange('disciplinaryId', e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            disabled={disciplinaryLoading}
          >
            <option value="">{disciplinaryLoading ? 'Loading disciplinary cases...' : 'Select case'}</option>
            {disciplinaryCases.map((disciplinaryCase) => (
              <option key={disciplinaryCase.id} value={disciplinaryCase.id}>
                {disciplinaryCase.StaffName} - {disciplinaryCase.TimeOfIncident || '-'}- {disciplinaryCase.DateOfIncident || '-'} - {disciplinaryCase.PolicyViolated || '-'}
              </option>
            ))}
          </select>
        </div>
      )}

      {selectedMeeting && (
        <div className="mt-6 rounded-xl border border-indigo-200 bg-indigo-50 p-4">
          <h3 className="text-sm font-semibold text-indigo-900 mb-2">Meeting Details</h3>
          <div className="text-sm text-indigo-900 space-y-1">
            <p><span className="font-medium">Date:</span> {toDisplayDate(selectedMeeting.Date)}</p>
            <p><span className="font-medium">Time:</span> {selectedMeeting.Time || '-'}</p>
            <p><span className="font-medium">Attendees:</span> {selectedMeeting.Attendees || '-'}</p>
            <p><span className="font-medium">Meeting Notes:</span> {selectedMeeting.MeetingNotes || '-'}</p>
            <p><span className="font-medium">Notes:</span> {selectedMeeting.Notes || '-'}</p>
          </div>
        </div>
      )}

      {message && (
        <p className="mt-4 text-sm text-indigo-700">{message}</p>
      )}

      <div className="mt-6 flex items-center justify-between">
        <p className="text-xs text-gray-500">Endpoint: /api/hashr</p>
        <button
          type="button"
          onClick={handleSaveCase}
          disabled={saving}
          className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
        >
          {saving ? 'Saving...' : 'Save Case'}
        </button>
      </div>

      <div className="mt-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Saved Cases</h3>
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="min-w-[1700px] w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">ID</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Staff</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Type</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Timeline</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Meeting</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Meeting Details</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Disciplinary</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Disciplinary Details</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Comments</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">HR Comments</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Staff View</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {casesLoading ? (
                <tr>
                  <td className="px-4 py-3 text-gray-500" colSpan={13}>Loading cases...</td>
                </tr>
              ) : savedCases.length === 0 ? (
                <tr>
                  <td className="px-4 py-3 text-gray-500" colSpan={13}>No cases yet.</td>
                </tr>
              ) : (
                savedCases.map((savedCase) => (
                  <tr key={savedCase.id}>
                    <td className="px-4 py-3">{savedCase.id}</td>
                    <td className="px-4 py-3">{savedCase.StaffName || savedCase.StaffId}</td>
                    <td className="px-4 py-3">{savedCase.CaseType || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusClassName(savedCase.Status)}`}>
                        {savedCase.Status || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">{toDisplayDate(savedCase.Timeline)}</td>
                    <td className="px-4 py-3">{savedCase.MeetingId || '-'}</td>
                    <td className="px-4 py-3 align-top min-w-[260px]">
                      <div className="space-y-1 text-xs text-gray-700">
                        <div>
                          <button
                            type="button"
                            onClick={() => toggleMeetingNotes(savedCase.id)}
                            className="inline-flex items-center rounded-md bg-indigo-100 px-2 py-1 text-xs font-semibold text-indigo-800 hover:bg-indigo-200"
                          >
                            {expandedMeetingNotes[savedCase.id] ? 'Hide MeetingNotes' : 'Show MeetingNotes'}
                          </button>
                          {expandedMeetingNotes[savedCase.id] && (
                            <p className="mt-2 whitespace-pre-wrap break-words rounded-md bg-gray-50 p-2">
                              <span className="font-semibold">MeetingNotes:</span> {savedCase.MeetingNotes || '-'}
                            </p>
                          )}
                        </div>
                        <p><span className="font-semibold">Date:</span> {toDisplayDate(savedCase.MeetingDate)}</p>
                        <p><span className="font-semibold">Time:</span> {savedCase.MeetingTime || '-'}</p>
                        <p><span className="font-semibold">Attendees:</span> {savedCase.MeetingAttendees || '-'}</p>
                        <p><span className="font-semibold">General Notes:</span> {savedCase.MeetingGeneralNotes || '-'}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">{savedCase.DisciplinaryId || '-'}</td>
                    <td className="px-4 py-3 align-top min-w-[260px]">
                      <div className="space-y-1 text-xs text-gray-700">
                        <p><span className="font-semibold">Staff:</span> {savedCase.DisciplinaryStaffName || '-'}</p>
                        <p><span className="font-semibold">Time:</span> {savedCase.DisciplinaryTimeOfIncident || '-'}</p>
                        <p><span className="font-semibold">Date:</span> {toDisplayDate(savedCase.DisciplinaryDateOfIncident)}</p>
                        <p><span className="font-semibold">Location Of Incident:</span> {savedCase.DisciplinaryLocationOfIncident || '-'}</p>
                        <p><span className="font-semibold">Policy:</span> {savedCase.DisciplinaryPolicyViolated || '-'}</p>
                        <p><span className="font-semibold">Witnesses:</span> {savedCase.DisciplinaryWitnesses || '-'}</p>
                        <p><span className="font-semibold">Witness Statements:</span> {savedCase.DisciplinaryWitnessStatements || '-'}</p>
                        <p><span className="font-semibold">Employee Perspective:</span> {savedCase.DisciplinaryEmployeePerspective || '-'}</p>
                        <p><span className="font-semibold">Agree With Incident:</span> {savedCase.DisciplinaryAgreeWithIncident || '-'}</p>
                        <p><span className="font-semibold">Explanation:</span> {savedCase.DisciplinaryExplanation || '-'}</p>
                        <p><span className="font-semibold">Steps Taken:</span> {savedCase.DisciplinaryStepsTaken || '-'}</p>
                        <p><span className="font-semibold">Description:</span> {savedCase.DisciplinaryDescription || '-'}</p>
                        <p><span className="font-semibold">Findings:</span> {savedCase.DisciplinaryFindings || '-'}</p>
                        <p><span className="font-semibold">Action:</span> {savedCase.DisciplinaryAction || '-'}</p>
                        <p><span className="font-semibold">Rationale:</span> {savedCase.DisciplinaryRationale || '-'}</p>
                        <p><span className="font-semibold">Corrective Actions:</span> {savedCase.DisciplinaryCorrectiveActions || '-'}</p>
                        <p><span className="font-semibold">Support Provided:</span> {savedCase.DisciplinarySupportProvided || '-'}</p>
                        <p><span className="font-semibold">Improvement Deadline:</span> {savedCase.DisciplinaryImprovementDeadline || '-'}</p>
                        <p><span className="font-semibold">Location:</span> {savedCase.DisciplinaryLocation || '-'}</p>
                        <p><span className="font-semibold">HR:</span> {savedCase.DisciplinaryHR || '-'}</p>
                        <p><span className="font-semibold">Anon:</span> {savedCase.DisciplinaryAnon || '-'}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top min-w-[320px] whitespace-pre-wrap break-words">{savedCase.Comments || '-'}</td>
                    <td className="px-4 py-3 align-top min-w-[320px] whitespace-pre-wrap break-words">{savedCase.HRComments || '-'}</td>
                    <td className="px-4 py-3">
                      {savedCase.StaffId ? (
                        <button
                          type="button"
                          onClick={() => handleOpenIndividualStaff(savedCase.StaffId)}
                          disabled={staffProfileLoading}
                          className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-500 disabled:opacity-60"
                        >
                          {staffProfileLoading ? 'Opening...' : 'Open Staff'}
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{toDisplayDate(savedCase.CreatedAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
